import { useState, useEffect, useCallback } from 'react';

/**
 * Hook pour gérer les notifications push
 * Prêt pour l'intégration avec le calendrier et les échéances
 */

// ============================================
// CONSTANTES
// ============================================

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';
const API_BASE = process.env.REACT_APP_API_URL || 'https://api.impartiale.com';

// Types de notifications supportés
export const NOTIFICATION_TYPES = {
  AUDIENCE: 'audience',       // Rappel d'audience
  ECHEANCE: 'echeance',       // Échéance à venir
  RAPPEL: 'rappel',           // Rappel général
  MESSAGE: 'message',         // Message/notification système
};

// Préférences par défaut
const DEFAULT_PREFERENCES = {
  enabled: false,
  audiences: true,
  echeances: true,
  rappels: true,
  messages: true,
  beforeAudience: 24, // heures avant une audience
  beforeEcheance: 48, // heures avant une échéance
  quietHoursStart: 22, // 22h
  quietHoursEnd: 7,    // 7h
};

// ============================================
// UTILITAIRES
// ============================================

/**
 * Convertit la clé VAPID en Uint8Array
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Vérifie si on est dans les heures calmes
 */
function isQuietHours(start, end) {
  const now = new Date().getHours();
  if (start > end) {
    // Ex: 22h - 7h
    return now >= start || now < end;
  }
  return now >= start && now < end;
}

// ============================================
// HOOK PRINCIPAL
// ============================================

export function useNotifications() {
  // États
  const [permission, setPermission] = useState('default');
  const [subscription, setSubscription] = useState(null);
  const [preferences, setPreferences] = useState(() => {
    const saved = localStorage.getItem('notification-preferences');
    return saved ? { ...DEFAULT_PREFERENCES, ...JSON.parse(saved) } : DEFAULT_PREFERENCES;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Support des notifications
  const isSupported = 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;

  // ============================================
  // INITIALISATION
  // ============================================

  useEffect(() => {
    if (!isSupported) return;

    // Vérifier la permission actuelle
    setPermission(Notification.permission);

    // Récupérer l'abonnement existant
    const getExistingSubscription = async () => {
      try {
        const registration = await navigator.serviceWorker.ready;
        const sub = await registration.pushManager.getSubscription();
        setSubscription(sub);
      } catch (err) {
        console.error('[Notifications] Erreur récupération abonnement:', err);
      }
    };

    getExistingSubscription();
  }, [isSupported]);

  // Sauvegarder les préférences
  useEffect(() => {
    localStorage.setItem('notification-preferences', JSON.stringify(preferences));
  }, [preferences]);

  // ============================================
  // DEMANDER LA PERMISSION
  // ============================================

  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      setError('Notifications non supportées sur ce navigateur');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await Notification.requestPermission();
      setPermission(result);

      if (result === 'granted') {
        console.log('[Notifications] Permission accordée');
        return true;
      } else if (result === 'denied') {
        setError('Notifications refusées. Activez-les dans les paramètres du navigateur.');
        return false;
      }
      
      return false;
    } catch (err) {
      console.error('[Notifications] Erreur permission:', err);
      setError('Erreur lors de la demande de permission');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported]);

  // ============================================
  // S'ABONNER AUX NOTIFICATIONS PUSH
  // ============================================

  const subscribe = useCallback(async () => {
    if (permission !== 'granted') {
      const granted = await requestPermission();
      if (!granted) return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const registration = await navigator.serviceWorker.ready;

      // Créer l'abonnement
      const sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: VAPID_PUBLIC_KEY ? urlBase64ToUint8Array(VAPID_PUBLIC_KEY) : undefined
      });

      setSubscription(sub);
      console.log('[Notifications] Abonné:', sub.endpoint);

      // Envoyer l'abonnement au serveur
      await sendSubscriptionToServer(sub);

      // Mettre à jour les préférences
      setPreferences(prev => ({ ...prev, enabled: true }));

      return sub;
    } catch (err) {
      console.error('[Notifications] Erreur abonnement:', err);
      setError('Erreur lors de l\'abonnement aux notifications');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [permission, requestPermission]);

  // ============================================
  // SE DÉSABONNER
  // ============================================

  const unsubscribe = useCallback(async () => {
    if (!subscription) return true;

    setIsLoading(true);
    setError(null);

    try {
      await subscription.unsubscribe();
      
      // Informer le serveur
      await removeSubscriptionFromServer(subscription);

      setSubscription(null);
      setPreferences(prev => ({ ...prev, enabled: false }));
      console.log('[Notifications] Désabonné');

      return true;
    } catch (err) {
      console.error('[Notifications] Erreur désabonnement:', err);
      setError('Erreur lors du désabonnement');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [subscription]);

  // ============================================
  // COMMUNICATION SERVEUR
  // ============================================

  const sendSubscriptionToServer = async (sub) => {
    try {
      const token = localStorage.getItem('token');
      
      await fetch(`${API_BASE}/api/notifications/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          subscription: sub.toJSON(),
          preferences: preferences
        })
      });
    } catch (err) {
      console.error('[Notifications] Erreur envoi serveur:', err);
      // Ne pas bloquer l'abonnement local si le serveur échoue
    }
  };

  const removeSubscriptionFromServer = async (sub) => {
    try {
      const token = localStorage.getItem('token');
      
      await fetch(`${API_BASE}/api/notifications/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          endpoint: sub.endpoint
        })
      });
    } catch (err) {
      console.error('[Notifications] Erreur suppression serveur:', err);
    }
  };

  // ============================================
  // NOTIFICATION LOCALE
  // ============================================

  const showLocalNotification = useCallback(async (title, options = {}) => {
    if (permission !== 'granted') {
      console.warn('[Notifications] Permission non accordée');
      return false;
    }

    // Vérifier les heures calmes
    if (isQuietHours(preferences.quietHoursStart, preferences.quietHoursEnd)) {
      console.log('[Notifications] Heures calmes, notification ignorée');
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      
      await registration.showNotification(title, {
        icon: '/icons/icon-192x192.png',
        badge: '/icons/icon-72x72.png',
        vibrate: [100, 50, 100],
        tag: options.tag || 'local-notification',
        ...options
      });

      return true;
    } catch (err) {
      console.error('[Notifications] Erreur notification locale:', err);
      return false;
    }
  }, [permission, preferences.quietHoursStart, preferences.quietHoursEnd]);

  // ============================================
  // NOTIFICATIONS SPÉCIALISÉES
  // ============================================

  /**
   * Notification pour une audience
   */
  const notifyAudience = useCallback(async (audience) => {
    if (!preferences.audiences) return false;

    return showLocalNotification(`📅 Audience: ${audience.juridiction || 'Tribunal'}`, {
      body: `${audience.affaire || 'Affaire'} - ${new Date(audience.date).toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit'
      })}`,
      tag: `audience-${audience.id}`,
      data: {
        type: NOTIFICATION_TYPES.AUDIENCE,
        url: `/affaires/${audience.affaireId}`,
        audienceId: audience.id
      },
      requireInteraction: true,
      actions: [
        { action: 'open', title: 'Voir détails' },
        { action: 'dismiss', title: 'Fermer' }
      ]
    });
  }, [preferences.audiences, showLocalNotification]);

  /**
   * Notification pour une échéance
   */
  const notifyEcheance = useCallback(async (echeance) => {
    if (!preferences.echeances) return false;

    return showLocalNotification(`⏰ Échéance: ${echeance.titre}`, {
      body: `À faire avant le ${new Date(echeance.date).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })}`,
      tag: `echeance-${echeance.id}`,
      data: {
        type: NOTIFICATION_TYPES.ECHEANCE,
        url: echeance.affaireId ? `/affaires/${echeance.affaireId}` : '/calendrier',
        echeanceId: echeance.id
      },
      actions: [
        { action: 'open', title: 'Voir' },
        { action: 'dismiss', title: 'Fermer' }
      ]
    });
  }, [preferences.echeances, showLocalNotification]);

  /**
   * Notification de rappel
   */
  const notifyRappel = useCallback(async (rappel) => {
    if (!preferences.rappels) return false;

    return showLocalNotification(`🔔 ${rappel.titre}`, {
      body: rappel.description || '',
      tag: `rappel-${rappel.id || Date.now()}`,
      data: {
        type: NOTIFICATION_TYPES.RAPPEL,
        url: rappel.url || '/',
        ...rappel.data
      }
    });
  }, [preferences.rappels, showLocalNotification]);

  // ============================================
  // GESTION DES PRÉFÉRENCES
  // ============================================

  const updatePreferences = useCallback((newPrefs) => {
    setPreferences(prev => {
      const updated = { ...prev, ...newPrefs };
      
      // Synchroniser avec le serveur si abonné
      if (subscription) {
        const token = localStorage.getItem('token');
        fetch(`${API_BASE}/api/notifications/preferences`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ preferences: updated })
        }).catch(console.error);
      }
      
      return updated;
    });
  }, [subscription]);

  // ============================================
  // RETOUR
  // ============================================

  return {
    // États
    isSupported,
    permission,
    subscription,
    preferences,
    isSubscribed: !!subscription,
    isLoading,
    error,

    // Actions principales
    requestPermission,
    subscribe,
    unsubscribe,

    // Notifications spécialisées
    showLocalNotification,
    notifyAudience,
    notifyEcheance,
    notifyRappel,

    // Préférences
    updatePreferences,
    
    // Types
    NOTIFICATION_TYPES
  };
}

export default useNotifications;
